<template>
    <div class="signin-page">
        <!-- Top Nav -->
        <div class="top-nav">
            <button class="back-btn" @click="handleBack">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
                    <path d="M15 18L9 12L15 6" stroke="#ff466a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Back
            </button>
            <div class="right-nav">
                <span class="nav-text" @click="$router.push('/reset-password')">Forgot password?</span>
                <button class="signup-top-btn" @click="$router.push('/signup')">Sign up</button>
            </div>
        </div>

        <div class="signin-container">
            <div class="logo-section mb-6">
                <img src="@/assets/logo_only.png" alt="XigFor Logo" class="logo" />
                <h1 class="brand-name">XigFor</h1>
            </div>

            <!-- Tabs -->
            <div class="tabs">
                <div
                    class="tab"
                    :class="{ active: activeTab === 'email' }"
                    @click="
                        activeTab = 'email'
                        errors.loginId = false
                    ">
                    Email
                </div>
                <div
                    class="tab"
                    :class="{ active: activeTab === 'phone' }"
                    @click="
                        activeTab = 'phone'
                        errors.loginId = false
                    ">
                    Phone
                </div>
            </div>

            <!-- Form Content -->
            <form class="signin-form" @submit.prevent>
                <!-- Email Input -->
                <div v-if="activeTab === 'email'">
                    <div class="input-container" :class="{ 'error-border': errors.loginId && !isEmailValid }">
                        <input type="email" placeholder="Enter a valid email address" v-model="formData.email" @input="errors.loginId = false" />
                    </div>
                    <div class="error-message mb-6" v-if="errors.loginId && !isEmailValid">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
                            <circle cx="12" cy="12" r="10" fill="#FF466A" />
                            <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                            <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                        </svg>
                        <span>Enter a valid email address</span>
                    </div>
                    <div class="spacer mb-6" v-else></div>
                </div>

                <!-- Phone Input -->
                <div v-if="activeTab === 'phone'">
                    <div class="input-container phone-input" :class="{ 'error-border': errors.loginId && !isPhoneValid }">
                        <PhoneAreaCodeSelect v-model="selectedPhoneAreaCode" @error-change="phoneAreaCodeError = $event" />
                        <div class="phone-divider"></div>
                        <input type="tel" placeholder="Phone number" v-model="formData.phone" @input="errors.loginId = false" />
                    </div>
                    <div v-if="phoneAreaCodeError" class="helper-text mb-6">
                        {{ phoneAreaCodeError }}
                    </div>
                    <div v-else class="spacer mb-6"></div>
                    <div class="error-message mb-6" v-if="errors.loginId && !isPhoneValid">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
                            <circle cx="12" cy="12" r="10" fill="#FF466A" />
                            <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                            <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                        </svg>
                        <span>Enter a valid phone number</span>
                    </div>
                    <div class="spacer mb-6" v-else></div>
                </div>

                <!-- Dynamic Secondary Input (Password OR OTP) -->
                <div v-if="!useOtpCode">
                    <label class="input-label">Password</label>
                    <div class="input-container" :class="{ 'error-border': errors.password && formData.password.length < 6 }">
                        <input :type="showPassword ? 'text' : 'password'" placeholder="Please enter password" v-model="formData.password" @input="errors.password = false" />
                        <img :src="showPassword ? require('@/assets/eye_open.png') : require('@/assets/eye.png')" class="eye-icon ml-auto" @click="showPassword = !showPassword" alt="Toggle password visibility" />
                    </div>
                    <button type="button" class="forgot-link" @click="$router.push('/reset-password')">Forgot password?</button>
                    <div class="error-message mb-8" v-if="errors.password && formData.password.length < 6">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
                            <circle cx="12" cy="12" r="10" fill="#FF466A" />
                            <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                            <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                        </svg>
                        <span>Incorrect password. Try again.</span>
                    </div>
                    <div class="spacer mb-8" v-else></div>
                </div>
                <div v-else>
                    <label class="input-label">Enter 6-digit code</label>
                    <div class="input-container verify-code-container" :class="{ 'error-border': errors.otp && formData.otpCode.length !== 6 }">
                        <input type="text" inputmode="numeric" maxlength="6" placeholder="Enter the verification code" v-model="formData.otpCode" @input="errors.otp = false" />
                        <button type="button" class="inline-send-btn" @click="sendCode" :disabled="countdown > 0" :class="{ cooldown: countdown > 0 }">
                            <span v-if="countdown > 0" class="countdown-text">{{ countdown }}s<br />Send</span>
                            <span v-else>Send</span>
                        </button>
                    </div>
                    <div class="error-message mb-8" v-if="errors.otp && formData.otpCode.length !== 6">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
                            <circle cx="12" cy="12" r="10" fill="#FF466A" />
                            <rect x="11" y="7" width="2" height="6" rx="1" fill="white" />
                            <rect x="11" y="15" width="2" height="2" rx="1" fill="white" />
                        </svg>
                        <span>Wrong code, please try again</span>
                    </div>
                    <div class="spacer mb-8" v-else></div>
                </div>

                <!-- Main Submit Action -->
                <button class="primary-btn mb-6" :class="{ active: isFormValid }" :disabled="isLoading" @click="handleSignIn">
                    <span v-if="!isLoading">Log In</span>
                    <div v-else class="loader"></div>
                </button>

                <!-- Divider -->
                <div class="divider mb-6">
                    <div class="line"></div>
                    <span>Or</span>
                    <div class="line"></div>
                </div>

                <!-- Switch Login Method -->
                <button
                    class="switch-method-btn mb-8"
                    @click="
                        useOtpCode = !useOtpCode
                        errors.password = false
                        errors.otp = false
                    ">
                    {{ useOtpCode ? 'Sign in with a password' : 'Sign in with a verification code' }}
                </button>

                <!-- Terms -->
                <div class="terms-section">
                    <label class="custom-checkbox mb-4">
                        <input type="checkbox" v-model="formData.agreeTerms" />
                        <span class="checkmark"></span>
                        <span class="label-text"> Agree to the <router-link to="/terms-of-service" class="link">"Xigfor User Terms"</router-link> and <router-link to="/privacy-policy" class="link">"Privacy Terms"</router-link> </span>
                    </label>

                    <label class="custom-checkbox">
                        <input type="checkbox" v-model="formData.agreeSMS" />
                        <span class="checkmark"></span>
                        <span class="label-text"> I agree to receive recurring SMS messages from XigFor related to account updates and service notifications. Message and data rates may apply. Reply Stop to cancel. </span>
                    </label>
                </div>
            </form>
        </div>

        <Toast v-if="toast.visible" :message="toast.message" :type="toast.type" @close="toast.visible = false" />
    </div>
</template>

<script>
import Toast from './Toast.vue'
import PhoneAreaCodeSelect from './PhoneAreaCodeSelect.vue'
import { buildApiUrl } from '@/config/api'

const AUTH_CODE_SCENE_LOGIN = 'LOGIN'

export default {
    name: 'SignIn',
    components: {
        Toast,
        PhoneAreaCodeSelect
    },
    data() {
        return {
            activeTab: 'email',
            useOtpCode: false,
            formData: {
                email: '',
                phone: '',
                password: '',
                otpCode: '',
                agreeTerms: false,
                agreeSMS: false
            },
            errors: {
                loginId: false,
                password: false,
                otp: false
            },
            showPassword: false,
            isLoading: false,
            countdown: 0,
            timer: null,
            selectedPhoneAreaCode: {
                name: 'United States, Canada',
                code: '+1',
                countryCode: 'US',
                flagEmoji: ''
            },
            phoneAreaCodeError: '',
            toast: {
                visible: false,
                message: '',
                type: 'info'
            }
        }
    },
    computed: {
        isEmailValid() {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)
        },
        isPhoneValid() {
            return /^\d{10,15}$/.test(this.formData.phone)
        },
        hasValidIdentifier() {
            return this.activeTab === 'email' ? this.isEmailValid : this.isPhoneValid
        },
        phoneAccount() {
            return `${this.selectedPhoneAreaCode.code}${this.formData.phone}`.replace(/\s+/g, '')
        },
        defaultFlagEmoji() {
            return '🌐'
        },
        isFormValid() {
            let isCredsValid = false
            if (this.useOtpCode) {
                isCredsValid = this.formData.otpCode.length === 6
            } else {
                isCredsValid = this.formData.password.length >= 6
            }
            return this.hasValidIdentifier && isCredsValid && this.formData.agreeTerms && this.formData.agreeSMS
        }
    },
    beforeDestroy() {
        if (this.timer) clearInterval(this.timer)
    },
    methods: {
        handleBack() {
            this.$router.push('/')
        },
        async fetchMemberInfo(token) {
            if (!token) return null

            const response = await fetch(buildApiUrl('/member/info'), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            })

            const data = await response.json()
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to fetch member info')
            }

            const memberInfo = data?.data || data || null
            if (memberInfo && typeof memberInfo === 'object') {
                localStorage.setItem('memberInfo', JSON.stringify(memberInfo))
                localStorage.setItem('userInfo', JSON.stringify(memberInfo))
                localStorage.setItem('profile', JSON.stringify(memberInfo))
            }

            return memberInfo
        },
        showToast(message, type = 'info') {
            this.toast.message = message
            this.toast.type = type
            this.toast.visible = true
        },
        async sendCode() {
            if (!this.hasValidIdentifier) {
                this.errors.loginId = true
                this.showToast('Please enter a valid ' + this.activeTab, 'error')
                return
            }

            const payload = {
                identityType: this.activeTab === 'email' ? 'EMAIL' : 'PHONE',
                scene: AUTH_CODE_SCENE_LOGIN
            }

            if (this.activeTab === 'email') {
                payload.email = this.formData.email
            } else {
                payload.phone = this.formData.phone
                payload.phoneCountryCode = this.selectedPhoneAreaCode.code
            }

            try {
                const response = await fetch(buildApiUrl('/sso/auth-code/send'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                })
                const data = await response.json()
                if (response.ok) {
                    const reasonCode = data?.data?.reasonCode || data?.reasonCode || ''
                    const reasonMessage = data?.data?.reasonMessage || data?.reasonMessage || ''
                    if (reasonCode === 'ACCOUNT_NOT_REGISTERED') {
                        this.showToast(reasonMessage || 'Please sign up first', 'warning')
                        setTimeout(() => {
                            this.$router.push('/signup')
                        }, 800)
                        return
                    }
                    const flowStatus = data?.data?.flowStatus || ''
                    if (flowStatus === 'REGISTER_REQUIRED') {
                        this.showToast(data?.data?.flowStatusDisplay || data?.message || 'Please sign up first', 'warning')
                        setTimeout(() => {
                            this.$router.push('/signup')
                        }, 800)
                        return
                    }
                    this.showToast('Verification code sent successfully', 'success')
                    this.startCountdown()
                } else {
                    this.showToast(data?.message || 'Failed to send verification code', 'error')
                }
            } catch (error) {
                console.error('Send code error:', error)
                this.showToast('Network error, please try again later', 'error')
            }
        },
        startCountdown() {
            this.countdown = 60
            this.timer = setInterval(() => {
                if (this.countdown > 0) {
                    this.countdown--
                } else {
                    clearInterval(this.timer)
                }
            }, 1000)
        },
        async handleSignIn() {
            this.errors.loginId = !this.hasValidIdentifier
            if (this.useOtpCode) {
                this.errors.otp = this.formData.otpCode.length !== 6
            } else {
                this.errors.password = this.formData.password.length < 6
            }

            if (!this.isFormValid || this.isLoading) {
                if (!this.formData.agreeTerms || !this.formData.agreeSMS) {
                    this.showToast('Please agree to terms to continue.', 'error')
                }
                return
            }

            this.isLoading = true
            try {
                const payload = {
                    identityType: this.activeTab === 'email' ? 'EMAIL' : 'PHONE'
                }

                if (this.activeTab === 'email') {
                    payload.email = this.formData.email
                } else {
                    payload.phone = this.formData.phone
                    payload.phoneCountryCode = this.selectedPhoneAreaCode.code
                }

                if (this.useOtpCode) {
                    payload.authCode = this.formData.otpCode
                } else {
                    payload.password = this.formData.password
                }

                const response = await fetch(buildApiUrl(this.useOtpCode ? '/sso/login/code' : '/sso/login/password'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                })

                const data = await response.json()

                if (response.ok && data?.code === 200) {
                    const token = data?.data?.token || data?.data?.accessToken || data?.token || data?.accessToken || ''
                    if (token) {
                        localStorage.setItem('token', token)
                        try {
                            await this.fetchMemberInfo(token)
                        } catch (memberError) {
                            console.error('Fetch member info error:', memberError)
                        }
                    }
                    console.log('Login success:', data)
                    this.showToast('Login successful!', 'success')
                    setTimeout(() => {
                        this.$router.push('/')
                    }, 1500)
                } else {
                    console.error('Login failed:', data)
                    this.showToast(data?.message || 'Login failed, please check your credentials', 'error')
                }
            } catch (error) {
                console.error('Login error:', error)
                this.showToast('Network error, please try again later', 'error')
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>

<style scoped>
.signin-page {
    background-color: #fff;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

@media (min-width: 768px) {
    .signin-page {
        background: #fff url('~@/assets/register_bg.png') no-repeat right center;
        background-size: cover;
    }
}

.top-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
}

.back-btn {
    display: flex;
    align-items: center;
    color: #ff466a;
    font-size: 16px;
    font-weight: 500;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

.right-nav {
    display: flex;
    align-items: center;
    font-size: 13px;
}

.nav-text {
    color: rgba(255, 70, 106, 1);
    font-size: 12px;
    font-weight: 500;
    margin-right: 12px;
    cursor: pointer;
}

.signup-top-btn {
    background-color: #ff466a;
    color: white;
    border-radius: 8px;
    padding: 8px 16px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    font-size: 14px;
}

.signin-container {
    flex: 1;
    max-width: 450px;
    margin: 0 auto;
    width: 100%;
    padding: 10px 24px 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.logo-section {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.logo {
    width: 89px;
    height: 89px;
    object-fit: contain;
    /* border-radius: 18px;
    box-shadow: 0 4px 12px rgba(255, 70, 106, 0.2); */
}

.brand-name {
    font-size: 28px;
    font-weight: 800;
    color: #1c273c;
    margin: 0;
}

/* Tabs */
.tabs {
    display: flex;
    width: 100%;
    margin-bottom: 24px;
}

.tab {
    flex: 1;
    text-align: center;
    padding-bottom: 12px;
    font-size: 16px;
    font-weight: 500;
    color: #999;
    cursor: pointer;
    position: relative;
}

.tab.active {
    color: #333;
    font-weight: bold;
}

.tab.active::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #000;
}

/* Form Inputs */
.signin-form {
    width: 100%;
}

.input-label {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #1c273c;
    margin-bottom: 8px;
}

.input-container {
    display: flex;
    align-items: center;
    width: 100%;
    border: 1px solid #eaeaea;
    border-radius: 10px;
    padding: 0 16px;
    height: 52px;
    transition: border-color 0.3s;
    background: #fff;
}

.input-container:focus-within {
    border-color: #ff466a;
}

.input-container input {
    flex: 1;
    border: none;
    font-size: 15px;
    outline: none;
    background: transparent;
    width: 100%;
}

.input-container input::placeholder {
    color: #ccc;
}

.verify-code-container {
    padding-right: 6px; /* Less padding to accommodate inner button */
}

.phone-divider {
    width: 1px;
    height: 20px;
    background-color: #eaeaea;
    margin: 0 12px;
}

.eye-icon {
    width: 18px;
    height: 18px;
    object-fit: contain;
    cursor: pointer;
}

/* Send OTP styling */
.inline-send-btn {
    background-color: #ffe4eb;
    border: none;
    border-radius: 8px;
    height: 40px;
    min-width: 64px;
    font-size: 14px;
    font-weight: 500;
    color: #ff466a;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 0 12px;
}

.inline-send-btn.cooldown {
    color: #888;
    background-color: #f6f6f6;
    cursor: not-allowed;
}

.inline-send-btn .countdown-text {
    font-size: 12px;
    line-height: 1.1;
    color: #333;
}

/* Buttons */
.primary-btn {
    width: 100%;
    height: 54px;
    border-radius: 12px;
    background-color: #efefef;
    color: #fff;
    font-size: 16px;
    font-weight: bold;
    border: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s;
}

.primary-btn.active {
    background: linear-gradient(270deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), linear-gradient(180deg, rgba(255, 158, 79, 1) 0%, rgba(255, 66, 107, 1) 100%), rgba(255, 70, 106, 1);
    box-shadow: 0px 1px 2px rgba(105, 81, 255, 0.05);
    color: #fff;
}

/* Divider */
.divider {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 16px;
    font-weight: 500;
}

.divider .line {
    height: 1px;
    background-color: #d8d8d8;
    width: 70px;
    margin: 0 16px;
}

/* Switch Method Text Button */
.switch-method-btn {
    width: 100%;
    text-align: center;
    background: transparent;
    border: none;
    color: #777;
    font-size: 15px;
    cursor: pointer;
}
.switch-method-btn:hover {
    color: #333;
}

.forgot-link {
    margin-top: 10px;
    margin-bottom: 16px;
    padding: 0;
    background: none;
    border: none;
    color: #ff466a;
    font-size: 13px;
    cursor: pointer;
    text-align: right;
    width: 100%;
}

/* Checkboxes */
.terms-section {
    width: 100%;
    margin-top: auto;
}
.custom-checkbox {
    display: flex;
    align-items: flex-start;
    position: relative;
    cursor: pointer;
    font-size: 12px;
    user-select: none;
}

.custom-checkbox input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.checkmark {
    position: relative;
    top: 1px;
    left: 0;
    height: 18px;
    width: 18px;
    background-color: #fff;
    border: 1.5px solid #a8a8a8;
    border-radius: 50%;
    flex-shrink: 0;
    margin-right: 10px;
    transition: all 0.2s;
    box-sizing: border-box;
}

.custom-checkbox input:checked ~ .checkmark {
    background-color: #fff;
    border-color: #ff4d6d;
}

.checkmark:after {
    content: '';
    position: absolute;
    display: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ff4d6d;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.custom-checkbox input:checked ~ .checkmark:after {
    display: block;
}

.label-text {
    color: #999;
    line-height: 1.4;
}

.link {
    color: #ff466a;
    text-decoration: none;
}

.ml-auto {
    margin-left: auto;
}
.mb-4 {
    margin-bottom: 16px;
}
.mb-6 {
    margin-bottom: 24px;
}
.mb-8 {
    margin-bottom: 32px;
}
.mr-1 {
    margin-right: 4px;
}

/* Loader */
.loader {
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid #fff;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.input-container.error-border {
    border-color: #ff466a;
}
.error-message {
    display: flex;
    align-items: center;
    color: #ff466a;
    font-size: 13px;
    width: 100%;
    margin-top: 8px;
}
.helper-text {
    color: #999;
    font-size: 12px;
    width: 100%;
    margin-top: 8px;
}
.spacer {
    height: 1px;
    visibility: hidden;
}
</style>
